import React, { Component } from 'react'
import { connect } from 'react-redux'
import { Redirect } from 'react-router-dom'
import moment from 'moment'
import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  Spinner,
  Badge,
  Progress,
  Button,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  FormGroup,
  Label,
  Input,
  UncontrolledDropdown,
  DropdownToggle,
  DropdownMenu,
  DropdownItem,
} from 'reactstrap'
import { People } from '@microsoft/mgt-react'
import Breadcrumbs from '../../../components/Common/Breadcrumb'
import { getRhDashboard } from '../../../store/ducks/humanResources'
import {
  monthRefDefault,
  mmYyyyFromMesRef,
} from '../../../utils/monthSelectOptions'
import { notifyError } from '../../../utils/notify'
import RhColaboradorModal from './RhColaboradorModal'
import RhBulkResultModal from './RhBulkResultModal'
import RhVisaoGeralChart from './RhVisaoGeralChart'
import RhDetalhamentoPanel from './RhDetalhamentoPanel'
import { fecharPontosEmMassa, lancarAbonoEmMassa, lancarFeriadoEmMassa } from './rhDashboardApi'

const RH_DASHBOARD_DEFAULT_MES_REF = monthRefDefault()

const MES_DEBOUNCE_MS = 500

const metricStyle = {
  fontSize: '0.7rem',
  color: '#74788d',
  letterSpacing: '0.02em',
  flex: '1 1 auto',
  minWidth: 0,
  paddingRight: 8,
}

const valueStyle = {
  fontSize: '0.875rem',
  fontWeight: 600,
  color: '#273351',
  whiteSpace: 'nowrap',
  flexShrink: 0,
  textAlign: 'right',
}

const metricRowStyle = {
  display: 'flex',
  alignItems: 'baseline',
  justifyContent: 'space-between',
  gap: 8,
  padding: '4px 0',
  borderBottom: '1px solid #f0f2f5',
}

const metricsBlockStyle = {
  display: 'flex',
  flexDirection: 'column',
  marginTop: 4,
}

const monthInputStyle = {
  padding: '6px 10px',
  borderRadius: 6,
  border: '1px solid #ced4da',
}

const deptSelectStyle = {
  ...monthInputStyle,
  minWidth: 180,
}

const sectionTitleStyle = {
  fontSize: '1.1rem',
  fontWeight: 700,
  color: '#273351',
  marginBottom: 16,
}

const viewToggleBtnStyle = (active) => ({
  fontSize: '0.8rem',
  fontWeight: active ? 600 : 400,
})

const mesRangeDefaults = (mesRef) => {
  const m = moment(mesRef, 'YYYY-MM', true)
  const start = m.clone().startOf('month')
  return {
    min: start.format('YYYY-MM-DD'),
    max: start.clone().endOf('month').format('YYYY-MM-DD'),
    start: start.format('YYYY-MM-DD'),
  }
}

class RhDashboard extends Component {
  constructor(props) {
    super(props)
    this.state = {
      mesRef: RH_DASHBOARD_DEFAULT_MES_REF,
      mesRefChanging: false,
      dptoFilter: '',
      detalhamentoPfId: '',
      visaoGeralMode: 'cards',
      graficoDimensao: 'colaborador',
      graficoMetrica: 'atividade',
      modalOpen: false,
      modalColaborador: null,
      bulkActionLoading: false,
      abonoModalOpen: false,
      abonoIni: mesRangeDefaults(RH_DASHBOARD_DEFAULT_MES_REF).start,
      abonoFim: mesRangeDefaults(RH_DASHBOARD_DEFAULT_MES_REF).start,
      abonoJustificativa: '',
      feriadoModalOpen: false,
      feriadoData: mesRangeDefaults(RH_DASHBOARD_DEFAULT_MES_REF).start,
      feriadoNome: 'Feriado',
      bulkResultOpen: false,
      bulkResultTitle: '',
      bulkResultSummary: '',
      bulkResultDetalhes: [],
      bulkResultTab: 'sucesso',
    }
    this.mesDebounceTimer = null
  }

  componentDidMount() {
    this.loadDashboardImmediate()
  }

  componentWillUnmount() {
    if (this.mesDebounceTimer) {
      clearTimeout(this.mesDebounceTimer)
    }
  }

  loadDashboardImmediate = () => {
    const { getRhDashboard } = this.props
    getRhDashboard(mmYyyyFromMesRef(this.state.mesRef))
  }

  scheduleMesRefLoad = (mesRef) => {
    if (this.mesDebounceTimer) {
      clearTimeout(this.mesDebounceTimer)
    }
    this.setState({
      mesRef,
      mesRefChanging: true,
      detalhamentoPfId: '',
      ...(() => {
        const range = mesRangeDefaults(mesRef)
        return { abonoIni: range.start, abonoFim: range.start, feriadoData: range.start }
      })(),
    })
    this.mesDebounceTimer = setTimeout(() => {
      this.mesDebounceTimer = null
      const { getRhDashboard } = this.props
      getRhDashboard(mmYyyyFromMesRef(mesRef))
    }, MES_DEBOUNCE_MS)
  }

  componentDidUpdate(prevProps) {
    const wasLoading = prevProps.humanResources.getRhDashboardIsLoading
    const isLoading = this.props.humanResources.getRhDashboardIsLoading
    if (wasLoading && !isLoading && this.state.mesRefChanging) {
      this.setState({ mesRefChanging: false })
    }
  }

  handleMesRefInputChange = (e) => {
    this.scheduleMesRefLoad(e.target.value)
  }

  handleMesAtual = () => {
    this.scheduleMesRefLoad(monthRefDefault())
  }

  handleDptoChange = (e) => {
    this.setState({ dptoFilter: e.target.value, detalhamentoPfId: '' })
  }

  handleDptoTodos = () => {
    this.setState({ dptoFilter: '', detalhamentoPfId: '' })
  }

  getDepartamentos = () => {
    const colaboradores = this.props.humanResources.rhDashboard?.colaboradores || []
    const set = new Set()
    colaboradores.forEach(c => {
      const nome = (c.dpto_nome || '').trim()
      if (nome) set.add(nome)
    })
    return Array.from(set).sort((a, b) => a.localeCompare(b, 'pt-BR'))
  }

  getColaboradoresFiltrados = () => {
    const colaboradores = this.props.humanResources.rhDashboard?.colaboradores || []
    const { dptoFilter } = this.state
    if (!dptoFilter) return colaboradores
    return colaboradores.filter(c => (c.dpto_nome || '').trim() === dptoFilter)
  }

  handleCardClick = (colaborador) => {
    this.setState({ detalhamentoPfId: String(colaborador.pf_id) })
    const el = document.getElementById('rh-detalhamento')
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }

  openColaboradorModal = (colaborador, e) => {
    if (e) e.stopPropagation()
    this.setState({ modalOpen: true, modalColaborador: colaborador })
  }

  closeColaboradorModal = () => {
    this.setState({ modalOpen: false, modalColaborador: null })
  }

  refreshDashboard = () => {
    this.setState({ mesRefChanging: false })
    this.loadDashboardImmediate()
  }

  showBulkResult = (title, data) => {
    const erros = Number(data.erros || 0)
    const pendentes = Number(data.pendentes || 0)
    const sobreposicao = Number(data.sobreposicao || 0)
    let bulkResultTab = 'sucesso'
    if (erros > 0) bulkResultTab = 'erro'
    else if (pendentes > 0 || sobreposicao > 0) bulkResultTab = 'pendente'

    this.setState({
      bulkResultOpen: true,
      bulkResultTitle: title,
      bulkResultSummary: data.msg || '',
      bulkResultDetalhes: data.detalhes || [],
      bulkResultTab,
    })
  }

  closeBulkResult = () => {
    this.setState({ bulkResultOpen: false })
  }

  runBulkFechar = async (sobreescreve) => {
    const { user } = this.props.auth
    const mesMmYyyy = mmYyyyFromMesRef(this.state.mesRef)
    const msg = sobreescreve
      ? `Reprocessar fechamento de todos os colaboradores em ${mesMmYyyy}?`
      : `Fechar ponto de todos os colaboradores em ${mesMmYyyy}?`
    if (!window.confirm(msg)) return

    this.setState({ bulkActionLoading: true })
    try {
      const data = await fecharPontosEmMassa(user.id, mesMmYyyy, sobreescreve)
      if (data?.ok === false) throw new Error(data.msg || 'Erro no fechamento em massa.')
      this.showBulkResult(
        sobreescreve ? `Reprocessar pontos — ${mesMmYyyy}` : `Fechar pontos — ${mesMmYyyy}`,
        data,
      )
      if ((data.fechados || 0) > 0) {
        this.refreshDashboard()
      }
    } catch (err) {
      notifyError(String(err))
    } finally {
      this.setState({ bulkActionLoading: false })
    }
  }

  handleAbonoConfirm = async () => {
    const { user } = this.props.auth
    const { mesRef, abonoIni, abonoFim, abonoJustificativa } = this.state
    const justificativa = abonoJustificativa.trim()
    if (!abonoIni || !abonoFim) {
      notifyError('Informe a data de início e a data de fim do abono.')
      return
    }
    if (abonoFim < abonoIni) {
      notifyError('A data de fim deve ser igual ou posterior à data de início.')
      return
    }
    if (!justificativa) {
      notifyError('Informe a justificativa do abono.')
      return
    }
    this.setState({ bulkActionLoading: true })
    try {
      const data = await lancarAbonoEmMassa(
        user.id,
        mmYyyyFromMesRef(mesRef),
        abonoIni,
        abonoFim,
        justificativa,
      )
      if (data?.ok === false) throw new Error(data.msg || 'Erro ao lançar abono.')
      this.setState({ abonoModalOpen: false })
      this.showBulkResult(`Lançar abono — ${mmYyyyFromMesRef(mesRef)}`, data)
      if ((data.inseridos || 0) > 0) {
        this.refreshDashboard()
      }
    } catch (err) {
      notifyError(String(err))
    } finally {
      this.setState({ bulkActionLoading: false })
    }
  }

  handleFeriadoConfirm = async () => {
    const { user } = this.props.auth
    const { mesRef, feriadoData, feriadoNome } = this.state
    if (!feriadoData) {
      notifyError('Informe a data do feriado.')
      return
    }
    this.setState({ bulkActionLoading: true })
    try {
      const data = await lancarFeriadoEmMassa(
        user.id,
        mmYyyyFromMesRef(mesRef),
        feriadoData,
        feriadoNome.trim() || 'Feriado',
      )
      if (data?.ok === false) throw new Error(data.msg || 'Erro ao lançar feriado.')
      this.setState({ feriadoModalOpen: false })
      this.showBulkResult(`Lançar feriado — ${mmYyyyFromMesRef(mesRef)}`, data)
      if ((data.inseridos || 0) > 0) {
        this.refreshDashboard()
      }
    } catch (err) {
      notifyError(String(err))
    } finally {
      this.setState({ bulkActionLoading: false })
    }
  }

  isCardsLoading = () => {
    const { getRhDashboardIsLoading } = this.props.humanResources
    return getRhDashboardIsLoading || this.state.mesRefChanging
  }

  renderMetricRow = (label, value, extra = null) => (
    <div style={metricRowStyle}>
      <span style={metricStyle}>{label}</span>
      <span style={valueStyle}>
        {value}
        {extra}
      </span>
    </div>
  )

  renderColaboradorCard = (c) => {
    const isSelected = String(this.state.detalhamentoPfId) === String(c.pf_id)
    const cargaHorariaZero = !(c.carga_horaria_s > 0)
    const percentualPct = c.percentual_carga_pct ?? Math.round((c.percentual_carga || 0) * 100)
    const acimaDaCarga = !cargaHorariaZero && percentualPct > 100
    const barValue = cargaHorariaZero ? 0 : Math.min(percentualPct, 100)
    const barColor = acimaDaCarga ? 'danger' : (percentualPct >= 100 ? 'success' : 'info')
    const percentualLabel = cargaHorariaZero
      ? (c.carga_realizada_s > 0 ? '—' : '0%')
      : `${percentualPct}%`
    const email = c.pf_emailProf || ''

    return (
      <Col key={c.pf_id} lg={4} md={6} sm={12} className="mb-4">
        <Card
          className={`h-100 shadow-sm ${isSelected ? 'border-primary' : ''}`}
          style={{ cursor: 'pointer', transition: 'box-shadow 0.15s ease' }}
          onClick={() => this.handleCardClick(c)}
        >
          <CardBody>
            <div className="d-flex align-items-start mb-3">
              <div className="mr-3" style={{ minWidth: 48 }}>
                {email ? (
                  <People showMax={1} peopleQueries={[email]} />
                ) : (
                  <div
                    className="rounded-circle bg-light d-flex align-items-center justify-content-center"
                    style={{ width: 40, height: 40, fontSize: '0.85rem', color: '#74788d' }}
                  >
                    {(c.pf_nomeExibicao || '?').charAt(0)}
                  </div>
                )}
              </div>
              <div className="flex-grow-1 min-width-0">
                <h6 className="mb-1 text-truncate">{c.pf_nomeExibicao}</h6>
                <div className="text-muted small text-truncate">{c.pf_cargo || '—'}</div>
                <div className="text-muted small text-truncate">{c.dpto_nome || '—'}</div>
              </div>
              <div className="d-flex flex-wrap justify-content-end ml-1" style={{ gap: 4, maxWidth: '45%' }}>
                <button
                  type="button"
                  className="btn btn-sm btn-link text-muted p-0"
                  title="Extrato e gestão"
                  aria-label="Extrato e gestão"
                  onClick={e => this.openColaboradorModal(c, e)}
                >
                  <i className="ri-file-list-3-line" style={{ fontSize: '1.15rem' }} />
                </button>
                {c.fechamento_ativo && (
                  <Badge color="info" pill style={{ fontSize: '0.65rem' }}>Fechado</Badge>
                )}
                {c.esta_online && (
                  <Badge color="success" pill>Online</Badge>
                )}
                {c.ponto_aberto && !c.esta_online && (
                  <Badge color="warning" pill>Ponto aberto</Badge>
                )}
              </div>
            </div>

            <div className="mb-2">
              <div className="d-flex justify-content-between align-items-center mb-1">
                <span style={{ ...metricStyle, textTransform: 'uppercase', fontSize: '0.65rem' }}>Carga do mês</span>
                <span
                  className="small font-weight-bold"
                  style={acimaDaCarga ? { color: '#ff5757' } : undefined}
                >
                  {percentualLabel}
                </span>
              </div>
              {!cargaHorariaZero && (
                <Progress value={barValue} color={barColor} />
              )}
              <div className="small text-muted mt-1" style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {c.carga_realizada_fmt}
                {' / '}
                {c.carga_horaria_s > 0 ? c.carga_horaria_fmt : '—'}
              </div>
            </div>

            <div style={metricsBlockStyle}>
              {this.renderMetricRow('Banco acumulado', c.banco_acumulado_fmt || '—')}
              {this.renderMetricRow(
                'Retroativos',
                c.pontos_retroativos || 0,
              )}
              {this.renderMetricRow('Bonificações', c.bonificacoes_fmt || '0h00min')}
            </div>

            {this.renderMetricRow(
              'Última entrada',
              c.ultima_entrada || '—',
              c.esta_online ? (
                <Badge color="success" className="ml-1" pill style={{ fontSize: '0.65rem' }}>online</Badge>
              ) : null,
            )}
          </CardBody>
        </Card>
      </Col>
    )
  }

  render() {
    const { user } = this.props.auth

    if (!user || user.perm_id !== 1) {
      return <Redirect to="/dashboard" />
    }

    const { getRhDashboardIsLoading, rhDashboard } = this.props.humanResources
    const colaboradores = this.getColaboradoresFiltrados()
    const todosColaboradores = rhDashboard?.colaboradores || []
    const totalColaboradores = rhDashboard?.colaboradores?.length || 0
    const departamentos = this.getDepartamentos()
    const { mesRef, dptoFilter, bulkActionLoading, abonoModalOpen, abonoIni, abonoFim, abonoJustificativa,
      feriadoModalOpen, feriadoData, feriadoNome,
      bulkResultOpen, bulkResultTitle, bulkResultSummary, bulkResultDetalhes, bulkResultTab,
      visaoGeralMode, graficoDimensao, graficoMetrica, detalhamentoPfId } = this.state
    const cardsLoading = this.isCardsLoading()
    const mesMmYyyy = mmYyyyFromMesRef(mesRef)
    const mesRange = mesRangeDefaults(mesRef)

    return (
      <div className="page-content">
        <Container fluid>
          <Breadcrumbs
            title="Dashboard RH"
            breadcrumbItems={[{ title: 'RH', link: '/recursos-humanos/dashboard' }]}
          />

          <RhBulkResultModal
            isOpen={bulkResultOpen}
            toggle={this.closeBulkResult}
            title={bulkResultTitle}
            summary={bulkResultSummary}
            detalhes={bulkResultDetalhes}
            activeTab={bulkResultTab}
            onTabChange={tab => this.setState({ bulkResultTab: tab })}
          />

          <Modal isOpen={abonoModalOpen} toggle={() => this.setState({ abonoModalOpen: false })} centered>
            <ModalHeader toggle={() => this.setState({ abonoModalOpen: false })}>
              Lançar abono em massa
            </ModalHeader>
            <ModalBody>
              <p className="text-muted small">
                Crédito de horas para todos os colaboradores com contrato em
                {' '}
                {mesMmYyyy}
                , em cada dia do período informado.
              </p>
              <FormGroup>
                <Label>Data de início</Label>
                <Input
                  type="date"
                  value={abonoIni}
                  min={mesRange.min}
                  max={mesRange.max}
                  onChange={e => this.setState({ abonoIni: e.target.value })}
                />
              </FormGroup>
              <FormGroup>
                <Label>Data de fim</Label>
                <Input
                  type="date"
                  value={abonoFim}
                  min={mesRange.min}
                  max={mesRange.max}
                  onChange={e => this.setState({ abonoFim: e.target.value })}
                />
              </FormGroup>
              <FormGroup className="mb-0">
                <Label>Justificativa</Label>
                <Input
                  type="textarea"
                  rows={3}
                  maxLength={500}
                  value={abonoJustificativa}
                  onChange={e => this.setState({ abonoJustificativa: e.target.value })}
                  placeholder="Motivo do abono (obrigatório)"
                />
              </FormGroup>
            </ModalBody>
            <ModalFooter>
              <Button color="light" onClick={() => this.setState({ abonoModalOpen: false })}>Cancelar</Button>
              <Button color="primary" disabled={bulkActionLoading} onClick={this.handleAbonoConfirm}>
                Lançar abono
                {bulkActionLoading && <Spinner size="sm" className="ml-2" />}
              </Button>
            </ModalFooter>
          </Modal>

          <Modal isOpen={feriadoModalOpen} toggle={() => this.setState({ feriadoModalOpen: false })} centered>
            <ModalHeader toggle={() => this.setState({ feriadoModalOpen: false })}>
              Lançar feriado em massa
            </ModalHeader>
            <ModalBody>
              <p className="text-muted small">
                Registra feriado para todos os colaboradores com contrato em
                {' '}
                {mesMmYyyy}
                .
              </p>
              <FormGroup>
                <Label>Data do feriado</Label>
                <Input
                  type="date"
                  value={feriadoData}
                  min={mesRange.min}
                  max={mesRange.max}
                  onChange={e => this.setState({ feriadoData: e.target.value })}
                />
              </FormGroup>
              <FormGroup className="mb-0">
                <Label>Nome do feriado</Label>
                <Input
                  type="text"
                  maxLength={120}
                  value={feriadoNome}
                  onChange={e => this.setState({ feriadoNome: e.target.value })}
                />
              </FormGroup>
            </ModalBody>
            <ModalFooter>
              <Button color="light" onClick={() => this.setState({ feriadoModalOpen: false })}>Cancelar</Button>
              <Button color="info" disabled={bulkActionLoading} onClick={this.handleFeriadoConfirm}>
                Lançar feriado
                {bulkActionLoading && <Spinner size="sm" className="ml-2" />}
              </Button>
            </ModalFooter>
          </Modal>

          <Row className="mb-4">
            <Col>
              <div
                className="d-flex justify-content-between align-items-end flex-wrap"
                style={{ gap: 12 }}
              >
                <div className="d-flex align-items-center flex-wrap" style={{ gap: 16 }}>
                  <div className="d-flex align-items-center flex-wrap" style={{ gap: 8 }}>
                    <div style={{ fontSize: 12, color: '#6c757d' }}>Mês:</div>
                    <input
                      type="month"
                      value={mesRef || monthRefDefault()}
                      onChange={this.handleMesRefInputChange}
                      disabled={cardsLoading || bulkActionLoading}
                      style={monthInputStyle}
                    />
                    <div style={{ fontSize: 12, color: '#6c757d' }}>Departamento:</div>
                    <select
                      value={dptoFilter}
                      onChange={this.handleDptoChange}
                      disabled={cardsLoading || departamentos.length === 0}
                      style={deptSelectStyle}
                    >
                      <option value="">Todos</option>
                      {departamentos.map(dpto => (
                        <option key={dpto} value={dpto}>{dpto}</option>
                      ))}
                    </select>
                    {dptoFilter && (
                      <button
                        type="button"
                        className="btn btn-sm btn-outline-secondary"
                        onClick={this.handleDptoTodos}
                      >
                        Todos
                      </button>
                    )}
                    <button
                      type="button"
                      className="btn btn-sm btn-outline-secondary"
                      onClick={this.handleMesAtual}
                      disabled={cardsLoading || bulkActionLoading}
                    >
                      Mês atual
                    </button>
                  </div>

                  <div className="d-flex align-items-center flex-wrap" style={{ gap: 6 }}>
                    <UncontrolledDropdown>
                      <DropdownToggle
                        size="sm"
                        color="secondary"
                        caret
                        disabled={cardsLoading || bulkActionLoading}
                      >
                        Ferramentas do administrador
                      </DropdownToggle>
                      <DropdownMenu>
                        <DropdownItem
                          disabled={bulkActionLoading}
                          onClick={() => this.runBulkFechar(false)}
                        >
                          Fechar pontos
                        </DropdownItem>
                        <DropdownItem
                          disabled={bulkActionLoading}
                          onClick={() => this.runBulkFechar(true)}
                        >
                          Reprocessar pontos
                        </DropdownItem>
                        <DropdownItem divider />
                        <DropdownItem
                          disabled={bulkActionLoading}
                          onClick={() => this.setState({ abonoModalOpen: true })}
                        >
                          Lançar abono
                        </DropdownItem>
                        <DropdownItem
                          disabled={bulkActionLoading}
                          onClick={() => this.setState({ feriadoModalOpen: true })}
                        >
                          Lançar feriado
                        </DropdownItem>
                      </DropdownMenu>
                    </UncontrolledDropdown>
                    {bulkActionLoading && <Spinner size="sm" color="primary" />}
                  </div>
                </div>

                {totalColaboradores > 0 && !cardsLoading && (
                  <div className="text-muted small">
                    {colaboradores.length}
                    {dptoFilter ? ` de ${totalColaboradores}` : ''}
                    {' '}
                    colaborador(es) com contrato em
                    {' '}
                    {rhDashboard?.mes || mesMmYyyy}
                  </div>
                )}
              </div>
            </Col>
          </Row>

          {cardsLoading && (
            <div className="text-center py-5">
              <Spinner color="primary" />
            </div>
          )}

          {!cardsLoading && this.props.humanResources.getRhDashboardError && (
            <div className="alert alert-danger">
              Erro ao carregar colaboradores. Tente novamente ou verifique a conexão com a API Segi.
            </div>
          )}

          {!cardsLoading && rhDashboard?.ok === false && (
            <div className="alert alert-warning">
              {rhDashboard.msg || 'Não foi possível carregar o dashboard.'}
            </div>
          )}

          {!cardsLoading && (
            <>
              <div className="d-flex flex-wrap justify-content-between align-items-center mb-3" style={{ gap: 12 }}>
                <h5 style={sectionTitleStyle} className="mb-0">Visão geral</h5>
                <div className="d-flex flex-wrap align-items-center" style={{ gap: 8 }}>
                  <div className="btn-group btn-group-sm">
                    <Button
                      color={visaoGeralMode === 'cards' ? 'primary' : 'light'}
                      style={viewToggleBtnStyle(visaoGeralMode === 'cards')}
                      onClick={() => this.setState({ visaoGeralMode: 'cards' })}
                    >
                      Cards
                    </Button>
                    <Button
                      color={visaoGeralMode === 'graficos' ? 'primary' : 'light'}
                      style={viewToggleBtnStyle(visaoGeralMode === 'graficos')}
                      onClick={() => this.setState({ visaoGeralMode: 'graficos' })}
                    >
                      Gráficos
                    </Button>
                  </div>
                  {visaoGeralMode === 'graficos' && (
                    <>
                      <select
                        value={graficoDimensao}
                        onChange={e => this.setState({ graficoDimensao: e.target.value })}
                        style={deptSelectStyle}
                      >
                        <option value="colaborador">Por colaborador</option>
                        <option value="projeto">Por projeto</option>
                      </select>
                      <select
                        value={graficoMetrica}
                        onChange={e => this.setState({ graficoMetrica: e.target.value })}
                        style={deptSelectStyle}
                      >
                        <option value="atividade">Horas em atividade</option>
                        <option value="retroativas">Horas retroativas</option>
                      </select>
                    </>
                  )}
                </div>
              </div>

              {visaoGeralMode === 'cards' && (
                <Row>
                  {colaboradores.map(c => this.renderColaboradorCard(c))}
                </Row>
              )}

              {visaoGeralMode === 'graficos' && (
                <Card className="border shadow-sm mb-4">
                  <CardBody>
                    <RhVisaoGeralChart
                      mesRef={mesRef}
                      dimensao={graficoDimensao}
                      metrica={graficoMetrica}
                      colaboradoresFiltrados={colaboradores}
                    />
                  </CardBody>
                </Card>
              )}
            </>
          )}

          {!cardsLoading && totalColaboradores > 0 && colaboradores.length === 0 && visaoGeralMode === 'cards' && (
            <div className="text-center text-muted py-5">
              Nenhum colaborador neste departamento para o mês selecionado.
            </div>
          )}

          {!cardsLoading && totalColaboradores === 0 && rhDashboard?.ok !== false && (
            <div className="text-center text-muted py-5">
              Nenhum colaborador com contrato vigente neste mês.
            </div>
          )}

          {!cardsLoading && totalColaboradores > 0 && (
            <div id="rh-detalhamento" className="mt-4 pt-2">
              <h5 style={sectionTitleStyle}>Detalhamento</h5>
              <RhDetalhamentoPanel
                mesRef={mesRef}
                mesRange={mesRange}
                colaboradores={todosColaboradores}
                selectedPfId={detalhamentoPfId}
                onPfIdChange={pfId => this.setState({ detalhamentoPfId: pfId })}
                onOpenExtrato={(c) => this.openColaboradorModal(c)}
              />
            </div>
          )}

          <RhColaboradorModal
            isOpen={this.state.modalOpen}
            toggle={this.closeColaboradorModal}
            colaborador={this.state.modalColaborador}
            mesRef={mesRef}
            onRefresh={this.refreshDashboard}
          />
        </Container>
      </div>
    )
  }
}

const mapStateToProps = state => ({
  humanResources: state.humanResources,
  auth: state.auth,
})

export default connect(mapStateToProps, { getRhDashboard })(RhDashboard)
